import React, {
  MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import './Spreadsheet.styles.css';
import canSubmitCorrection from '../../utils/canSubmitCorrection';
import errorResponses from '../../errorResponses';
import getTokensForCorrection from '../../utils/getTokensForCorrection';
import useDisableBodyScrollSwipe from '../hooks/useDisableBodyScrollSwipe';
import { AgGridReact } from 'ag-grid-react';
import { AppContext } from '../../App';

import {
  CellEditingStoppedEvent,
  GetRowIdParams,
  IRowNode,
  RowClassParams,
  CellFocusedEvent,
  GridApi,
} from 'ag-grid-community';
import { DEFAULT_COLUMNS_IN_GRID_COUNT, GRID_HEIGHT, GRID_FILTER_PARAMS } from './config';
import {
  ICoordinates,
  IResponseData,
  IResultsType,
  TFieldMeta,
} from '../../types/spreadsheet-types';
import { IToken } from '../../types/my-types';
import { MarkDispatchContext, MarkStateContext } from '../../context/MarkContext';
import { SpreadsheetProps } from './SpreadsheetTypes';
import { confColumns } from './confColumns';
import { confRows } from './confRows';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { useCreateCorrection } from '../hooks/useCreateCorrection';
import { useExtractionSpreadsheet } from '../hooks/useExtractionSpreadsheet';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import deepEqual from 'fast-deep-equal';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  filteredExtractionIdsAtom,
  validatingExtractionIdsAtom,
  validationNotReadyExtractionIdsAtom,
} from '../../atoms/spreadsheet-atoms';

interface RowType {
  extractionId: string;
  [key: string]: any;
}

const Spreadsheet: React.FC<SpreadsheetProps> = ({
  extractionBatch,
  resultsType,
  isVerified = false,
}) => {
  const { dpid, id, eid } = useParams<{
    dpid: string;
    id: string;
    eid: string;
  }>();

  if (!eid) {
    throw new Error('eid is not defined');
  }

  const { showReviewSpreadsheet, setToast, accessibility } = useContext(AppContext);

  const {
    activeField,
    gridShowValidationErrors,
    fieldListFocused,
    tokens,
    pageIndex,
    showValidations,
    spreadsheetFocusedCell,
    validationsEnabled,
    validationSummaryByExtractionId,
    visibleExtractionIds,
  } = useContext(MarkStateContext);

  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  const cache = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<ICoordinates>({ y: GRID_HEIGHT });
  // resultsType now controlled by parent via props
  const [shouldSubmitEdit, setShouldSubmitEdit] = useState(true);
  const MIN_GRID_HEIGHT = 160; // don't shrink below this for UX
  const setFilteredExtractionIds = useSetAtom(filteredExtractionIdsAtom);
  const validatingExtractionIds = useAtomValue(validatingExtractionIdsAtom);
  const validationNotReadyExtractionIds = useAtomValue(validationNotReadyExtractionIdsAtom);

  useDisableBodyScrollSwipe();

  // Add effect to set accessibility mode
  useEffect(() => {
    document.documentElement.setAttribute('data-accessibility', accessibility ? 'true' : 'false');
    return () => {
      document.documentElement.removeAttribute('data-accessibility');
    };
  }, [accessibility]);

  // Update the fieldMeta on the row
  // When a correction already exists on another page and a correction is issued on that field on another
  // page, this updates the fieldMeta in the grid so the grid does not jump to the page the correction
  const updateFieldMeta = (
    gridApi: GridApi,
    extractionId: string,
    field: string,
    metaUpdate: Partial<TFieldMeta>
  ) => {
    const node = gridApi.getRowNode(extractionId);
    if (!node || !node.data) return;
    const oldData = node.data;
    const updatedRow = {
      ...oldData,
      fieldMeta: {
        ...oldData.fieldMeta,
        [field]: {
          ...oldData.fieldMeta[field],
          ...metaUpdate,
        },
      },
    };

    gridApi.applyTransaction({ update: [updatedRow] });
  };

  // Sync the focused row when the extraction changes (e.g. doc stepper or
  // grid-initiated navigation). In React 19, navigate() uses startTransition
  // so the eid URL param updates asynchronously. When the grid navigates to a
  // different extraction, we pass the desired pageIndex through React Router's
  // navigation state so it can be re-applied here after the transition
  // completes, preventing stale effects from resetting it to 0.
  useEffect(() => {
    const navState = location.state as { pageIndex?: number; colId?: string } | null;
    const hasNavState = navState && (navState.pageIndex !== undefined || navState.colId);

    if (hasNavState) {
      const payload: Record<string, any> = { rowId: eid };
      if (navState.pageIndex !== undefined && navState.pageIndex >= 0) {
        payload.pageIndex = navState.pageIndex;
      }
      if (navState.colId) {
        payload.colId = navState.colId;
        payload.fieldListFocused = false;
      }
      dispatch({
        type: 'SET_SPREADSHEET_FOCUSED_CELL',
        payload,
      });
      // Clear the navigation state so it doesn't re-apply on subsequent renders
      navigate(location.pathname + location.search, { replace: true, state: {} });
    } else if (spreadsheetFocusedCell?.rowId !== eid) {
      dispatch({
        type: 'SET_SPREADSHEET_FOCUSED_CELL',
        payload: { rowId: eid },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  const rowClassRules = useMemo(
    () => ({
      'row-highlight': (params: RowClassParams) =>
        spreadsheetFocusedCell?.rowId != null &&
        params.data?.extractionId === spreadsheetFocusedCell?.rowId,
    }),
    [spreadsheetFocusedCell]
  );

  const mutationCreateCorrection = useCreateCorrection(true, true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Correction Saved',
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['extraction', eid]
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveCorrection,
      });
    },
  });

  const extractionIndex: number = extractionBatch.extractions.findIndex(
    (e) => getIdFromUrl(e) === eid
  );

  // FIXME: Types
  const retData: IResponseData[] = useExtractionSpreadsheet(
    extractionBatch.extractions,
    resultsType
  );

  const isLoading: boolean = retData.some((result: IResponseData) => result.isLoading);
  const isFetching: boolean = retData.some((result: IResponseData) => result.isFetching);
  const isSuccess: boolean = retData.every((result: IResponseData) => result.isSuccess);

  const rowData = useMemo(() => {
    if (!isSuccess || !retData) {
      return [];
    }

    return confRows(
      retData,
      validationSummaryByExtractionId,
      new Set(validatingExtractionIds),
      new Set(validationNotReadyExtractionIds)
    );
  }, [
    retData,
    isSuccess,
    gridShowValidationErrors,
    activeField,
    validationSummaryByExtractionId,
    validatingExtractionIds,
    validationNotReadyExtractionIds,
  ]);

  const newVisibleIds = useMemo(() => {
    if (!isSuccess || !rowData) {
      return [];
    }
    return rowData.map((r: any) => r.extractionId);
  }, [rowData, isSuccess]);

  // Helper function to safely access grid API
  const getGridApi = useCallback(() => {
    if (!gridRef.current?.api) {
      console.warn('Grid API not available');
      return null;
    }
    return gridRef.current.api;
  }, []);

  // cancel editing if the user changes from corrected to normalized
  useEffect(() => {
    if (resultsType === 'normalized') {
      getGridApi()?.stopEditing(true);
    }
  }, [resultsType, getGridApi]);

  // Put grid into editing state when tokens are selected
  useEffect(() => {
    if (activeField === '' || fieldListFocused || isVerified) {
      return;
    }
    const gridApi = getGridApi();
    if (!gridApi) return;

    const cellDefs = gridApi.getEditingCells();
    if (cellDefs) {
      let newToken = '';

      if (tokens.length !== 0) {
        // state.tokens are immutable, so deep copy for sort
        let mytokens: IToken[] = [...tokens];
        newToken = mytokens
          .sort((a: IToken, b: IToken) => a.bbox[0].x - b.bbox[0].x)
          .map((t: IToken) => t.content)
          .join(' ');
      }

      if (newToken !== '') {
        gridApi.stopEditing(true);
        setShouldSubmitEdit(false);

        // Find the correct row to place into editable mode by extractionId and not by displayed index
        let displayedRow: RowType | null = null;
        let displayedRowIndex = -1;

        // Get the total number of displayed rows
        const displayedRowCount = gridApi.getDisplayedRowCount();

        // Iterate through displayed rows in order to find the correct index
        for (let i = 0; i < displayedRowCount; i++) {
          const rowNode = gridApi.getDisplayedRowAtIndex(i);
          if (rowNode?.data?.extractionId === eid) {
            displayedRow = rowNode.data as RowType;
            displayedRowIndex = i;
            break;
          }
        }

        if (displayedRow && displayedRowIndex !== -1) {
          // Get the row node and update its data directly (bypassing transaction system to avoid filter re-evaluation)
          const rowNode = gridApi.getRowNode(eid);
          if (rowNode && rowNode.data) {
            // Directly modify the data object without triggering events
            rowNode.data[activeField] = newToken;
          }

          // Start editing the cell - it will now show the token value
          gridApi.startEditingCell({
            rowIndex: displayedRowIndex,
            colKey: activeField,
          });
        }
      } else {
        gridApi.stopEditing(true);
      }
    }
  }, [tokens, activeField, fieldListFocused, getGridApi]);

  const columnDefs = useMemo(() => {
    // Return empty columns if grid is not yet initialized
    if (!gridRef.current) {
      return [];
    }

    return confColumns(
      retData,
      eid,
      gridRef.current,
      extractionIndex,
      validationSummaryByExtractionId,
      extractionBatch,
      spreadsheetFocusedCell,
      showValidations,
      accessibility,
      dpid || ''
    );
  }, [
    retData,
    extractionIndex,
    validationSummaryByExtractionId,
    extractionBatch,
    gridRef.current,
    showValidations,
    accessibility,
    dpid,
  ]);

  const cellFocused = useCallback(
    (cell: CellFocusedEvent) => {
      const rowNode = cell.api.getDisplayedRowAtIndex(cell.rowIndex!);
      const rowId = rowNode?.data?.extractionId;
      const columnId = typeof cell.column === 'string' ? cell.column : cell.column?.getColId();
      if (!rowId) {
        console.log('Focus moved away from any row');
        return;
      }

      const fieldName = typeof cell.column === 'string' ? null : cell.column?.getColDef()?.field;

      // Pull the field metadata from the row node and get the page index
      const fieldMeta = columnId ? rowNode?.data?.fieldMeta?.[columnId] : undefined;

      dispatch({
        type: 'SET_SPREADSHEET_FOCUSED_CELL',
        payload: {
          rowId,
          colId: columnId,
          fieldListFocused: false,
          ...(fieldMeta?.pageNumber !== undefined && fieldMeta.pageNumber !== -1
            ? { pageIndex: fieldMeta.pageNumber }
            : {}),
        },
      });

      if (fieldName && rowId !== eid) {
        dispatch({ type: 'RESET_PAGE', payload: { resetFieldAndPage: false } });
        // Pass the desired pageIndex and colId through navigation state so the
        // eid useEffect can re-apply them after the transition completes.
        // Without this, the eid useEffect would overwrite activeField to
        // undefined, preventing the cell from entering edit mode.
        const desiredPage = fieldMeta?.pageNumber;
        const navState: Record<string, any> = {};
        if (desiredPage !== undefined && desiredPage !== -1) {
          navState.pageIndex = desiredPage;
        }
        if (columnId) {
          navState.colId = columnId;
        }
        navigate(`/data-pools/${dpid}/extraction-batch/${id}/review/${rowId}`, {
          state: Object.keys(navState).length > 0 ? navState : undefined,
        });
      }
    },
    [eid, dpid, id, dispatch, navigate]
  );

  const cellValueChanged = useCallback(
    (event: any) => {
      if (!shouldSubmitEdit) return;

      const fieldData = retData[extractionIndex]?.data?.results[event.colDef.field] || [];
      const myTokens = getTokensForCorrection(event.newValue, fieldData, tokens);

      if (canSubmitCorrection(event.oldValue, event.newValue, fieldData.tokens, myTokens)) {
        const newCorrection = { content: event.newValue || '', tokens: myTokens || [] };

        mutationCreateCorrection.mutate({
          extraction: eid,
          field: event.colDef.field,
          correction: newCorrection,
        });

        const gridApi = getGridApi();
        if (!gridApi) return;

        const totalColumns = columnDefs.length - DEFAULT_COLUMNS_IN_GRID_COUNT;

        // Only update the field meta if the tokens on this page different
        const rowNode = gridApi.getRowNode(eid);
        const currentAllFieldMeta = rowNode?.data?.fieldMeta || {};
        const fieldMetaObject = currentAllFieldMeta[event.colDef.field!];
        const currentPageNumber = fieldMetaObject.pageNumber || 0;

        if (currentPageNumber !== pageIndex) {
          updateFieldMeta(gridApi, eid, event.colDef.field!, {
            pageNumber: pageIndex,
          });
        }
        dispatch({
          type: 'SAVE_FIELD',
          payload: { fieldsCount: totalColumns, fieldListFocused: false },
        });
      }
    },
    [
      shouldSubmitEdit,
      retData,
      extractionIndex,
      tokens,
      mutationCreateCorrection,
      eid,
      columnDefs,
      dispatch,
      getGridApi,
    ]
  );

  const cellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent) => {
      if (event.oldValue === event.newValue) {
        const fieldData = retData[extractionIndex]?.data?.results[event.colDef.field!] || [];
        const myTokens = getTokensForCorrection(event.newValue, fieldData, tokens);

        if (canSubmitCorrection(event.oldValue, event.newValue, fieldData.tokens, myTokens)) {
          const newCorrection = { content: event.newValue, tokens: myTokens };

          const gridApi = getGridApi();
          if (!gridApi) return;

          // Only update the field meta if the tokens on this page different
          const rowNode = gridApi.getRowNode(eid);
          const currentAllFieldMeta = rowNode?.data?.fieldMeta || {};
          const fieldMetaObject = currentAllFieldMeta[event.colDef.field!];

          const currentPageNumber = fieldMetaObject.pageNumber || 0;
          if (currentPageNumber !== pageIndex) {
            updateFieldMeta(gridApi, eid, event.colDef.field!, {
              pageNumber: pageIndex,
            });
          }

          mutationCreateCorrection.mutate({
            extraction: eid,
            field: event.colDef.field,
            correction: newCorrection,
          });

          const totalColumns = columnDefs.length - DEFAULT_COLUMNS_IN_GRID_COUNT;

          dispatch({
            type: 'SAVE_FIELD',
            payload: { fieldsCount: totalColumns, fieldListFocused: false },
          });
        }
      }
    },
    [
      retData,
      extractionIndex,
      tokens,
      mutationCreateCorrection,
      eid,
      columnDefs,
      dispatch,
      getGridApi,
    ]
  );

  // Update data on grid
  useEffect(() => {
    if (isFetching || !isSuccess) {
      return;
    }

    const newVisibleIds = rowData?.map((r: any) => r.extractionId);
    if (!deepEqual(newVisibleIds, visibleExtractionIds)) {
      dispatch({
        type: 'SET_VISIBLE_EXTRACTIONS',
        payload: newVisibleIds,
      });
    }
  }, [rowData, isFetching, isSuccess, visibleExtractionIds, dispatch]);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    // Center the selected column on the grid
    const gridApi = getGridApi();
    if (activeField && gridApi) {
      gridApi.ensureColumnVisible(activeField);
    }
  }, [isFetching, activeField, getGridApi]);

  // FIXME: Move to hook?
  const dragHandler = (mouseDownEvent: MouseEvent) => {
    mouseDownEvent.preventDefault();
    const startSize = size;
    const startPosition = { y: mouseDownEvent.pageY };
    // Visual feedback
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'row-resize';

    const onMouseMove = (mouseMoveEvent: any) => {
      const raw = startSize.y - startPosition.y + mouseMoveEvent.pageY;
      const clamped = Math.max(MIN_GRID_HEIGHT, raw);
      setSize({ y: clamped });
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      document.body.style.cursor = prevCursor;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp, { once: true });
  };

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.extractionId;
  }, []);

  // Add an event listener for the enter key to submit the edit
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setShouldSubmitEdit(true);
    }
  }, []);

  const visibleErrorSet = useMemo(
    () => new Set(gridShowValidationErrors),
    [gridShowValidationErrors]
  );
  const hasVisibleErrorsById = useMemo(() => {
    // Map extractionId  →  boolean
    const map: Record<string, boolean> = {};
    validationSummaryByExtractionId.forEach(({ extractionId, validationErrorsList }) => {
      if (visibleErrorSet.size === 0) {
        map[extractionId] = true; // show all rows
      } else {
        map[extractionId] = validationErrorsList.some((err) => visibleErrorSet.has(err));
      }
    });
    return map;
  }, [validationSummaryByExtractionId, visibleErrorSet]);

  const isExternalFilterPresent = useCallback(
    () => gridShowValidationErrors.length > 0,
    [gridShowValidationErrors]
  );

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<RowType>) => {
      // rows with no extractionId (shouldn't happen) are shown by default
      return node.data ? (hasVisibleErrorsById[node.data.extractionId] ?? true) : true;
    },
    [hasVisibleErrorsById]
  );

  useEffect(() => {
    const gridApi = getGridApi();
    if (gridApi?.isAnyFilterPresent()) {
      gridApi.onFilterChanged();
    }
  }, [hasVisibleErrorsById, getGridApi]);

  // Callback to update filtered extraction IDs when grid filters change
  const onFilterChanged = useCallback(() => {
    const gridApi = getGridApi();
    if (!gridApi) return;

    const filteredIds: string[] = [];
    gridApi.forEachNodeAfterFilter((node) => {
      if (node.data?.extractionId) {
        filteredIds.push(node.data.extractionId);
      }
    });

    setFilteredExtractionIds(filteredIds);
  }, [getGridApi, setFilteredExtractionIds]);

  // Initialize filtered extraction IDs when rowData changes (no filters applied)
  useEffect(() => {
    if (!rowData || rowData.length === 0) return;

    const gridApi = getGridApi();
    if (gridApi) {
      // If grid is ready, use the filter callback
      onFilterChanged();
    } else {
      // Otherwise, just use all row data
      const allIds = rowData.map((r: any) => r.extractionId).filter(Boolean);
      setFilteredExtractionIds(allIds);
    }
  }, [rowData, getGridApi, onFilterChanged, setFilteredExtractionIds]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const defaultColDef = useMemo(
    () => ({
      editable: resultsType === 'corrected' && !isVerified,
      resizable: true,
      filter: 'agSetColumnFilter',
      filterParams: GRID_FILTER_PARAMS,
      sortable: false,
    }),
    [resultsType, isVerified]
  );

  const gridOptions = useMemo(
    () => ({
      context: {
        suppressHeaderFocus: true,
        spreadsheetFocusedCell,
      },
      pagination: true,
      paginationPageSizeSelector: [5, 10, 20, 50, 100, 1000],
    }),
    [spreadsheetFocusedCell]
  );

  // Make sure the current extraction is visible in the grid
  useEffect(() => {
    if (!eid || !gridRef.current?.api || !isSuccess || isFetching || extractionIndex === -1) return;

    const gridApi = gridRef.current.api;

    // Get current pagination page size
    const pageSize = gridApi.paginationGetPageSize();

    // Calculate which page the row should be on
    const targetPage = Math.floor(extractionIndex / pageSize);

    // Get current page
    const currentPage = gridApi.paginationGetCurrentPage();

    // If the row is not on the current page, go to the correct page
    if (currentPage !== targetPage) {
      gridApi.paginationGoToPage(targetPage);
    }

    // Ensure the row is visible
    const rowNode = gridApi.getRowNode(eid);
    if (rowNode) {
      gridApi.ensureNodeVisible(rowNode);
    }
  }, [eid, extractionBatch.extractions, extractionIndex, isSuccess, isFetching]);

  if (!showReviewSpreadsheet) return null;

  return (
    <>
      <div>
        <div
          ref={wrapperRef}
          className="ag-theme-balham ag-glynt"
          data-cy="div-spreadsheet-wrapper"
          style={{ height: `${size.y}px`, overflowX: 'scroll' }}
        >
          {isLoading ? (
            <div className="grid-loading-wrapper">
              <div className="grid-loading-wrapper-transparent"></div>
              <div className="grid-loading-wrapper-spinner">
                <CircularProgress />
              </div>
            </div>
          ) : null}
          <AgGridReact
            ref={gridRef}
            // onCellClicked={cellClicked}
            onCellFocused={cellFocused}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            rowClassRules={rowClassRules}
            rowData={rowData}
            columnDefs={columnDefs}
            onCellValueChanged={cellValueChanged}
            onCellEditingStopped={cellEditingStopped}
            onFilterChanged={onFilterChanged}
            suppressNoRowsOverlay
            suppressPropertyNamesCheck
            suppressMaintainUnsortedOrder
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            alwaysShowHorizontalScroll={true}
            rowBuffer={20}
            suppressScrollOnNewData={true}
            animateRows={false}
            tooltipShowDelay={700}
            gridOptions={gridOptions}
            // tooltipHideDelay={1200}
          ></AgGridReact>
        </div>
      </div>
      <hr
        onMouseDown={dragHandler}
        className="dragGrid"
        style={{ cursor: 'row-resize', userSelect: 'none' }}
        aria-label="Resize table height"
      />
    </>
  );
};

export default Spreadsheet;
